import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle, ExternalLink, Globe, Filter, Bell } from "lucide-react";
import euSearchIcon from "@/assets/eu-search-icon.png";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import BreadcrumbJsonLd from "@/components/BreadcrumbJsonLd";
import GuideBreadcrumb from "@/components/GuideBreadcrumb";
import LanguageSelector from "@/components/LanguageSelector";

export default function ConsultareTed() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Come Consultare il TED Efficacemente"
        description="Guida pratica per consultare Tenders Electronic Daily (TED). Scopri come cercare bandi europei, usare i filtri avanzati e monitorare le gare UE."
        path="/it/guide/consultare-ted"
        alternates={[
          { lang: "it", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
          { lang: "x-default", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
        ]}
      />
      <BreadcrumbJsonLd
        items={[
          { name: "Home", href: "https://tedmonitor.eu/" },
          { name: "Guide", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
          { name: "Consultare TED", href: "https://tedmonitor.eu/it/guide/consultare-ted" },
        ]}
      />

      {/* Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-mark.png" alt="TED Monitor" className="h-8 w-8" />
            <span className="font-semibold text-lg text-primary">TED Monitor</span>
          </Link>
          <div className="flex items-center gap-4">
            <LanguageSelector />
            <Button asChild>
              <Link to="/auth">Inizia ora</Link>
            </Button>
          </div>
        </div>
      </header>

      <main>
        <GuideBreadcrumb
          items={[
            { label: "Guide", href: "/it/guide/consultare-ted" },
            { label: "Consultare TED" },
          ]}
        />
        {/* Hero Section */}
        <section className="py-8 md:py-12 bg-gradient-to-b from-primary/5 to-background">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="mb-4">
              <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium">
                Guida
              </span>
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-primary mb-4 leading-tight">
              Come Consultare il TED (Tenders Electronic Daily) Efficacemente
            </h1>
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              TED (Tenders Electronic Daily) è il portale ufficiale dell'Unione Europea per la pubblicazione dei bandi
              di gara sopra soglia. Consultarlo efficacemente richiede conoscere la struttura dei dati, i filtri
              disponibili e le strategie di ricerca ottimali.
            </p>

            {/* Internal links */}
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/it/guide/codici-cpv"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Guida ai Codici CPV
              </Link>
              <Link
                to="/it/guide/bandi-vs-preinformazione"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Bandi vs Pre-informazione
              </Link>
              <Link
                to="/it/guide/filtri-ricerca-avanzata"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Filtri di ricerca avanzata
              </Link>
              <Link
                to="/it/monitoraggio-bandi-ue"
                className="px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-medium hover:bg-primary/15"
              >
                Monitoraggio appalti europei
              </Link>
            </div>
          </div>
        </section>

        {/* Cos'è TED */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Globe className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Cos'è il TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              <a
                href="https://ted.europa.eu"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline inline-flex items-center gap-1"
              >
                Tenders Electronic Daily <ExternalLink className="h-3 w-3" />
              </a>{" "}
              è il supplemento della Gazzetta Ufficiale dell'Unione Europea dedicato agli appalti pubblici. Ogni giorno
              vengono pubblicati centinaia di avvisi provenienti da tutti i 27 Stati membri.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Il TED include:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Bandi di gara (contract notices)",
                "Avvisi di pre-informazione (prior information notices)",
                "Avvisi di aggiudicazione (contract award notices)",
                "Rettifiche e modifiche contrattuali",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Come cercare */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <img src={euSearchIcon} alt="EU Search" className="h-6 w-6 object-contain flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Come cercare bandi sul TED</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il TED offre una ricerca testuale e filtri avanzati. Tuttavia, l'interfaccia può risultare complessa per
              chi non ha familiarità con la struttura dei dati europei.
            </p>

            <p className="text-muted-foreground mb-3 text-sm">Strategie efficaci:</p>
            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Usa i codici CPV per identificare categorie merceologiche precise",
                "Combina filtri per paese, tipo di procedura e data di pubblicazione",
                "Imposta ricerche salvate per monitoraggi ricorrenti",
                "Verifica sempre la scadenza e la lingua originale del bando",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-green-600 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="mt-4">
              <Link to="/it/guide/codici-cpv" className="text-primary hover:underline font-medium text-sm">
                Approfondisci i codici CPV →
              </Link>
            </p>
          </div>
        </section>

        {/* Filtri avanzati */}
        <section className="py-6 md:py-8">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Filter className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Filtri avanzati disponibili</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Il TED permette di filtrare i risultati secondo molteplici criteri. Conoscerli ti aiuta a ridurre il
              rumore e concentrarti sulle opportunità rilevanti.
            </p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4">
              {[
                "Codici CPV",
                "Paese dell'ente",
                "Tipo di procedura",
                "Valore stimato",
                "Data pubblicazione",
                "Scadenza presentazione",
              ].map((item) => (
                <div key={item} className="p-2 bg-background rounded-lg border text-center">
                  <p className="font-medium text-foreground text-xs">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Limiti del TED */}
        <section className="py-6 md:py-8 bg-muted/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <div className="flex items-start gap-3 mb-4">
              <Bell className="h-6 w-6 text-primary flex-shrink-0 mt-1" />
              <h2 className="text-xl md:text-2xl font-semibold text-foreground">Limiti della consultazione manuale</h2>
            </div>

            <p className="text-muted-foreground mb-4 leading-relaxed text-sm">
              Consultare il TED manualmente ogni giorno richiede tempo e può portare a perdere opportunità. Le
              limitazioni principali includono:
            </p>

            <ul className="space-y-1.5 text-muted-foreground text-sm mb-4">
              {[
                "Nessun sistema di alert automatico integrato",
                "Interfaccia non ottimizzata per workflow professionali",
                "Difficoltà nel confrontare bandi tra paesi diversi",
                "Mancanza di analisi aggregate e trend",
              ].map((item) => (
                <li key={item} className="flex items-center gap-2">
                  <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                  {item}
                </li>
              ))}
            </ul>

            <p className="text-muted-foreground leading-relaxed text-sm">
              TED Monitor risolve questi limiti offrendo alert automatici, filtri avanzati e un'interfaccia progettata
              per il procurement professionale.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-8 md:py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-xl md:text-2xl font-semibold mb-4">
              Vuoi monitorare il TED in modo più efficiente?
            </h2>
            <p className="text-base mb-6 opacity-90">
              TED Monitor ti permette di ricevere alert automatici sui nuovi bandi e consultare i dati TED in formato
              strutturato e comparabile.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="default" variant="secondary" asChild>
                <Link to="/it/prezzi" className="gap-2">
                  Vedi i prezzi
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              <Button
                size="default"
                variant="outline"
                className="bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary"
                asChild
              >
                <Link to="/auth">Inizia ora</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 bg-background">
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src="/logo-mark.png" alt="TED Monitor" className="h-6 w-6" />
              <span className="text-sm text-muted-foreground">© {new Date().getFullYear()} TED Monitor</span>
            </div>
            <nav className="flex flex-wrap gap-6 text-sm">
              <Link to="/it/prezzi" className="text-muted-foreground hover:text-foreground">
                Prezzi
              </Link>
              <Link to="/it/fonte-dei-dati" className="text-muted-foreground hover:text-foreground">
                Fonte dati
              </Link>
              <Link to="/privacy-policy" className="text-muted-foreground hover:text-foreground">
                Privacy Policy
              </Link>
              <Link to="/terms-conditions" className="text-muted-foreground hover:text-foreground">
                Termini
              </Link>
            </nav>
          </div>
        </div>
      </footer>
    </div>
  );
}
